import React, { Suspense, lazy } from "react";
import "./App.css";

import {
    BrowserRouter,
    Navigate,
    Route,
    Routes,
    useLocation,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { DesignProvider } from "./context/DesignContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { TransactionProvider } from "./context/TransactionContext";
import TransactionReceiptModal from "./components/transactions/TransactionReceiptModal";
import AppErrorBoundary from "./components/AppErrorBoundary";

const LegalPage = lazy(() => import("./pages/LegalPage"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const VerifyAccount = lazy(() => import("./pages/VerifyAccount"));
const P2PExchangePage = lazy(() => import("./pages/P2PExchangePage"));
const P2PPartnerPage = lazy(() => import("./pages/P2PPartnerPage"));
const P2PAdminPage = lazy(() => import("./pages/P2PAdminPage"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const InvestorDashboard = lazy(() => import("./pages/InvestorDashboard"));
const AuthorizedAgentDashboard = lazy(() => import("./pages/AuthorizedAgentDashboard"));
const CardsPage = lazy(() => import("./pages/CardsPage"));
const DepositsPage = lazy(() => import("./pages/DepositsPage"));
const MonCashDepositPage = lazy(() => import("./pages/MonCashDepositPage"));
const WithdrawalsPage = lazy(() => import("./pages/WithdrawalsPage"));
const WalletTransferPage = lazy(() => import("./pages/WalletTransferPage"));
const TransferHubPage = lazy(() => import("./pages/TransferHubPage"));
const CalculatorPage = lazy(() => import("./pages/CalculatorPage"));
const KYCPage = lazy(() => import("./pages/KYCPage"));
const RechargePage = lazy(() => import("./pages/RechargePage"));
const CryptoPage = lazy(() => import("./pages/CryptoPage"));
const NowPaymentsPage = lazy(() => import("./pages/NowPaymentsPage"));
const NowPaymentsAdminPage = lazy(() => import("./pages/NowPaymentsAdminPage"));
const BillsPage = lazy(() => import("./pages/BillsPage"));
const BillPayPage = lazy(() => import("./pages/BillPayPage"));
const StreamingPage = lazy(() => import("./pages/StreamingPage"));
const AllServicesPage = lazy(() => import("./pages/AllServicesPage"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const PromoReferralPage = lazy(() => import("./pages/PromoReferralPage"));
const WizzGiftCards = lazy(() => import("./pages/WizzGiftCards"));
const TransferMethodPage = lazy(() => import("./pages/TransferMethodPage"));
const RecargaDominicanaPage = lazy(() => import("./pages/RecargaDominicanaPage"));
const RecargaDominicanaProductPage = lazy(() => import("./pages/RecargaDominicanaProductPage"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PartnerCardsPage = lazy(() => import("./pages/PartnerCardsPage"));
const PartnerMethodTransferPage = lazy(() => import("./pages/PartnerMethodTransferPage"));
const PartnerAutoTransferPage = lazy(() => import("./pages/PartnerAutoTransferPage"));
const PartnerManualTransferPage = lazy(() => import("./pages/PartnerManualTransferPage"));
const PartnerDigitalTransferPage = lazy(() => import("./pages/PartnerDigitalTransferPage"));
const MkApiDocsPage = lazy(() => import("./pages/MkApiDocsPage"));
import GlobalDynamicDesign from "./components/GlobalDynamicDesign";
const SupportApp = lazy(() => import("./pages/SupportApp"));
const InfluencerDashboard = lazy(() => import("./pages/InfluencerDashboard"));
const InfluencerLanding = lazy(() => import("./pages/InfluencerLanding"));

function RootRedirect() {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center text-cyan-300">
                <div className="font-display text-lg">
                    Chajman...
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    const role = String(user.role || "")
        .trim()
        .toLowerCase();

    if (role === "admin") {
        return <Navigate to="/admin" replace />;
    }

    if (role === "investor") {
        return <Navigate to="/investor" replace />;
    }

    if (role === "investor_agent") {
        return <Navigate to="/investor-agent" replace />;
    }

    if (role === "partner") {
        return <Navigate to="/partner/dashboard" replace />;
    }

    if (role === "influencer") {
        return <Navigate to="/influencer" replace />;
    }

    return <Navigate to="/dashboard" replace />;
}

function ClientDashboardRouter() {
    return <ClientDashboard />;
}

function AppLoadingFallback() {
    return (
        <div className="mk-app-loading" role="status" aria-live="polite">
            <img src="/logo-transparent.png" alt="MKTRANSFÈ" className="mk-app-loading-logo" />
            <div className="mk-app-loading-spinner" />
            <div className="mk-app-loading-text">MKTRANSFÈ ap louvri...</div>
        </div>
    );
}


function RouteErrorBoundary({ children }) {
    const location = useLocation();
    return (
        <AppErrorBoundary resetKey={`${location.pathname}${location.search}`}>
            {children}
        </AppErrorBoundary>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <NotificationProvider>
                    <DesignProvider>
                        <TransactionProvider>

                            <GlobalDynamicDesign />

                            <BrowserRouter>
                                <RouteErrorBoundary>
                                <Suspense fallback={<AppLoadingFallback />}>
                                <Routes>
                                    <Route path="/legal/:document" element={<LegalPage />} />
                                    <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                                    <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                                    <Route path="/refund-policy" element={<Navigate to="/legal/refunds" replace />} />
                                    <Route
                                        path="/"
                                        element={<RootRedirect />}
                                    />

                                    <Route
                                        path="/login"
                                        element={<Login />}
                                    />

                                    <Route
                                        path="/support-app"
                                        element={<SupportApp />}
                                    />

                                    <Route
                                        path="/i/:slug"
                                        element={<InfluencerLanding />}
                                    />

                                    <Route
                                        path="/influencer"
                                        element={
                                            <ProtectedRoute role="influencer">
                                                <InfluencerDashboard />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/partners"
                                        element={<PartnersPage />}
                                    />

                                    <Route
                                        path="/mk-api/docs"
                                        element={<MkApiDocsPage />}
                                    />

                                    <Route
                                        path="/register"
                                        element={<Register />}
                                    />

                                    <Route
                                        path="/verify"
                                        element={<VerifyAccount />}
                                    />

                                    <Route
                                        path="/dashboard"
                                        element={
                                            <ProtectedRoute>
                                                <ClientDashboardRouter />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/ht-dashboard"
                                        element={
                                            <Navigate
                                                to="/dashboard"
                                                replace
                                            />
                                        }
                                    />

                                    <Route
                                        path="/agent"
                                        element={
                                            <Navigate
                                                to="/dashboard"
                                                replace
                                            />
                                        }
                                    />

                                    <Route
                                        path="/kyc"
                                        element={
                                            <ProtectedRoute role="client">
                                                <KYCPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/cards"
                                        element={
                                            <ProtectedRoute role="client">
                                                <CardsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/deposits"
                                        element={
                                            <ProtectedRoute role="client">
                                                <DepositsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/deposits/moncash"
                                        element={
                                            <ProtectedRoute role="client">
                                                <MonCashDepositPage provider="moncash" />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/deposits/natcash"
                                        element={
                                            <ProtectedRoute role="client">
                                                <MonCashDepositPage provider="natcash" />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/withdrawals"
                                        element={
                                            <ProtectedRoute role="client">
                                                <WithdrawalsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/transfers"
                                        element={
                                            <ProtectedRoute role="client">
                                                <TransferHubPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/transfers/wallet"
                                        element={
                                            <ProtectedRoute role="client">
                                                <WalletTransferPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/transfers/:methodSlug"
                                        element={
                                            <ProtectedRoute role="client">
                                                <TransferHubPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/gift-cards"
                                        element={
                                            <ProtectedRoute role="client">
                                                <WizzGiftCards />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/recarga-dominicana"
                                        element={
                                            <ProtectedRoute role="client">
                                                <RecargaDominicanaPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/recarga-dominicana/:section/:productId"
                                        element={
                                            <ProtectedRoute>
                                                <RecargaDominicanaProductPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/calculator"
                                        element={<CalculatorPage />}
                                    />


                                    <Route path="/crypto/nowpayments" element={<ProtectedRoute role="client"><NowPaymentsPage /></ProtectedRoute>} />
                                    <Route path="/admin/nowpayments" element={<ProtectedRoute role="admin"><NowPaymentsAdminPage /></ProtectedRoute>} />

                                    <Route
                                        path="/crypto"
                                        element={
                                            <ProtectedRoute role="client">
                                                <CryptoPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/recharge"
                                        element={
                                            <ProtectedRoute role="client">
                                                <RechargePage />
                                            </ProtectedRoute>
                                        }
                                    />


                                    <Route
                                        path="/all-services"
                                        element={
                                            <ProtectedRoute role="client">
                                                <AllServicesPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/streaming"
                                        element={
                                            <ProtectedRoute role="client">
                                                <StreamingPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/bills"
                                        element={
                                            <ProtectedRoute role="client">
                                                <BillsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/bills/:billId"
                                        element={
                                            <ProtectedRoute role="client">
                                                <BillPayPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/settings"
                                        element={
                                            <ProtectedRoute role="client">
                                                <SettingsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/promo"
                                        element={
                                            <ProtectedRoute role="client">
                                                <PromoReferralPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route path="/p2p-exchange" element={<ProtectedRoute role="client"><P2PExchangePage /></ProtectedRoute>} />
                                    <Route path="/admin/p2p-exchange" element={<ProtectedRoute role="admin"><P2PAdminPage /></ProtectedRoute>} />
                                    <Route
                                        path="/admin"
                                        element={
                                            <ProtectedRoute role="admin">
                                                <AdminDashboard />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/investor"
                                        element={
                                            <ProtectedRoute role="investor">
                                                <InvestorDashboard />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/investor-agent"
                                        element={
                                            <ProtectedRoute role="investor_agent">
                                                <AuthorizedAgentDashboard />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/partner/dashboard"
                                        element={
                                            <ProtectedRoute role="partner">
                                                <PartnerDashboard />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route path="/partner/p2p" element={<ProtectedRoute role="partner"><P2PPartnerPage /></ProtectedRoute>} />

                                    <Route
                                        path="/partner/cards"
                                        element={
                                            <ProtectedRoute role="partner">
                                                <PartnerCardsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route path="/partner/transfers" element={<ProtectedRoute role="partner"><PartnerMethodTransferPage /></ProtectedRoute>} />
                                    <Route
                                        path="/partner/automatic-transfer/:service"
                                        element={
                                            <ProtectedRoute role="partner">
                                                <PartnerAutoTransferPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/partner/manual-transfer/:service"
                                        element={
                                            <ProtectedRoute role="partner">
                                                <PartnerManualTransferPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/partner/digital-transfer/:service"
                                        element={
                                            <ProtectedRoute role="partner">
                                                <PartnerDigitalTransferPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    <Route
                                        path="/p/:slug"
                                        element={<DynamicPage />}
                                    />

                                    <Route
                                        path="*"
                                        element={
                                            <Navigate
                                                to="/"
                                                replace
                                            />
                                        }
                                    />
                                </Routes>
                                </Suspense>
                                </RouteErrorBoundary>
                                <TransactionReceiptModal />
                            </BrowserRouter>

                        </TransactionProvider>
                    </DesignProvider>
                </NotificationProvider>
            </AuthProvider>
        </div>
    );
}

export default App;